/**
 * LLD-SPA-041 — "the layer renders from the state machine of LLD-IDN-150. It holds no second
 * list of states." The states, the edges and every rule number cited on a transition below are
 * design/03-auth-and-credentials.md §3.1i's table and its thirteen rules, transcribed as a
 * reducer rather than re-derived. This is the ONE list; `AuthLayer.tsx` switches on it and
 * nothing else in `src/layers/auth/` keeps a second copy.
 *
 * `a-finishreg` and `a-orgblocked` are NOT states in this reducer, though both are §3.1i table
 * rows and both are built (`src/screens/a-finishreg/`, `src/screens/a-orgblocked/`). Both are
 * entered from "any authenticated call" (a `401 no_identity` / `403 ORG_BLOCKED` response) —
 * i.e. AFTER this layer has already handed off — so they belong to whatever owns the
 * authenticated app's response handling (`src/layers/shell/OrgResolver.tsx` for the first login,
 * and eventually a global interceptor for mid-session arrivals), never to "becoming
 * authenticated." `a-session` (a failed `/auth/refresh`, §3.1j) stays here because a refresh
 * failure hands control BACK to this layer, at sign-in — the opposite direction.
 *
 * `a-verify` / `a-verifyexp` are rule 4's "outside the shell and outside authentication
 * entirely" — not part of this reducer either; each mounts standalone, addressed by the link's
 * own token (`src/screens/a-verify/AddressVerificationEntry.tsx`).
 */

export type SignInBanner = 'password-changed' | 'signed-out-everywhere'

export type AuthMachineState =
  | { screen: 'a-signup' }
  | { screen: 'a-signin'; banner?: SignInBanner; invitation?: string }
  | { screen: 'a-confirm'; email: string; invitation?: string }
  | { screen: 'a-resendconfirm'; email?: string }
  | { screen: 'a-mfa'; continuation: string; email: string }
  | { screen: 'a-mfaenrol'; continuation?: string; email: string }
  | { screen: 'a-forgot' }
  | { screen: 'a-reset'; email: string }
  | { screen: 'a-resetnew'; email: string }
  | { screen: 'a-changepw' }
  | { screen: 'a-invite'; invitation: string }
  | { screen: 'a-invitedead' }
  | { screen: 'accepting-invitation'; invitation: string }
  | { screen: 'a-session' }
  | { screen: 'authenticated' }

export type AuthMachineAction =
  // a-signup (rule 9: sign-up never leads directly to enrolment or tokens)
  | { type: 'SIGNED_UP'; email: string; invitation?: string }
  | { type: 'GO_TO_SIGNIN' }
  // a-signin -> a-signup: the reverse of GO_TO_SIGNIN. `a-signup`'s own spec calls it "the first
  // screen of the product," and it takes an organisation name directly (self-serve tenant
  // creation) — a real, built path with no prior entry point anywhere in the app.
  | { type: 'GO_TO_SIGNUP' }
  // a-confirm
  | { type: 'CONFIRMED' }
  // a-resendconfirm
  | { type: 'RESENT'; email: string }
  // a-signin (identitySignIn's three real outcomes, SignInResult / §3.1j routing table)
  | { type: 'SIGNED_IN'; email: string; challenge?: { kind: 'SOFTWARE_TOKEN_MFA' | 'MFA_SETUP'; continuation: string } }
  // rule 11: an invite token survives the sign-in flow in memory, "re-clicked if it does not"
  | { type: 'GO_TO_SIGNIN_TO_ACCEPT'; invitation: string }
  | { type: 'ACCOUNT_UNCONFIRMED'; email: string }
  | { type: 'PASSWORD_RESET_REQUIRED' }
  | { type: 'GO_TO_FORGOT' }
  // a-mfa / a-mfaenrol
  | { type: 'CHALLENGE_ANSWERED' } // -> authenticated
  | { type: 'CHALLENGE_EXPIRED' } // rule: back to sign-in, "start again"
  | { type: 'ENROLLED' } // -> authenticated (rule 9: enrolment inside sign-in always yields a session)
  // a-forgot / a-reset / a-resetnew
  | { type: 'RESET_REQUESTED'; email: string }
  | { type: 'GO_TO_RESETNEW' }
  | { type: 'PASSWORD_RESET' } // rule 10 + ResetPasswordRequest: -> sign-in, banner
  // a-changepw (rule 7)
  | { type: 'PASSWORD_CHANGED' }
  // a-invite / a-invitedead
  | { type: 'INVITE_DEAD' }
  | { type: 'INVITE_ACCEPTED_EXISTING_USER' } // identityAcceptInvitation, after sign-in

export function authReducer(state: AuthMachineState, action: AuthMachineAction): AuthMachineState {
  switch (action.type) {
    case 'SIGNED_UP':
      return { screen: 'a-confirm', email: action.email, invitation: action.invitation }
    case 'GO_TO_SIGNIN':
      return { screen: 'a-signin' }
    case 'GO_TO_SIGNUP':
      return { screen: 'a-signup' }
    case 'CONFIRMED':
      // LLD-API-041: ConfirmSignUp returns no tokens. Every path into the product goes through
      // sign-in (§3.1i rule 9).
      return { screen: 'a-signin' }
    case 'RESENT':
      return { screen: 'a-confirm', email: action.email }
    case 'SIGNED_IN': {
      if (action.challenge) {
        return action.challenge.kind === 'SOFTWARE_TOKEN_MFA'
          ? { screen: 'a-mfa', continuation: action.challenge.continuation, email: action.email }
          : { screen: 'a-mfaenrol', continuation: action.challenge.continuation, email: action.email }
      }
      const pendingInvitation = state.screen === 'a-signin' ? state.invitation : undefined
      return pendingInvitation ? { screen: 'accepting-invitation', invitation: pendingInvitation } : { screen: 'authenticated' }
    }
    case 'GO_TO_SIGNIN_TO_ACCEPT':
      return { screen: 'a-signin', invitation: action.invitation }
    case 'ACCOUNT_UNCONFIRMED':
      // §3.1i rule (SignInResult): ACCOUNT_UNCONFIRMED never renders as "wrong password".
      return { screen: 'a-resendconfirm', email: action.email }
    case 'PASSWORD_RESET_REQUIRED':
      return { screen: 'a-forgot' }
    case 'GO_TO_FORGOT':
      return { screen: 'a-forgot' }
    case 'CHALLENGE_ANSWERED':
    case 'ENROLLED':
      return { screen: 'authenticated' }
    case 'CHALLENGE_EXPIRED':
      // rule (ChallengeAnswerRequest): an expired continuation restarts from sign-in.
      return { screen: 'a-signin' }
    case 'RESET_REQUESTED':
      return { screen: 'a-reset', email: action.email }
    case 'GO_TO_RESETNEW':
      return state.screen === 'a-reset' ? { screen: 'a-resetnew', email: state.email } : state
    case 'PASSWORD_RESET':
      // rule 10 / ResetPasswordRequest: every session ends; sign-in with the reason stated.
      return { screen: 'a-signin', banner: 'password-changed' }
    case 'PASSWORD_CHANGED':
      // rule 7: a-changepw always lands on a-signin.
      return { screen: 'a-signin', banner: 'signed-out-everywhere' }
    case 'INVITE_DEAD':
      return { screen: 'a-invitedead' }
    case 'INVITE_ACCEPTED_EXISTING_USER':
      return { screen: 'authenticated' }
    default:
      return state
  }
}

export const initialAuthState: AuthMachineState = { screen: 'a-signin' }
